import * as THREE from 'three'

const vertexPars = /* glsl */`
  varying vec3 vTerrainWorldPosition;
  varying vec3 vTerrainWorldNormal;
`

const vertexNormal = /* glsl */`
  vTerrainWorldNormal = normalize(transpose(inverse(mat3(modelMatrix))) * objectNormal);
`

const vertexPosition = /* glsl */`
  vTerrainWorldPosition = (modelMatrix * vec4(transformed, 1.0)).xyz;
`

const fragmentPars = /* glsl */`
  varying vec3 vTerrainWorldPosition;
  varying vec3 vTerrainWorldNormal;
  uniform vec3 uNavy;
  uniform vec3 uDeepNavy;
  uniform vec3 uContourBlue;
  uniform vec3 uRust;
  uniform vec3 uIvory;
  uniform float uElevationMin;
  uniform float uElevationMax;
  uniform float uContourSpacing;
  uniform float uContourWidth;
  uniform float uRustSlopeStart;
  uniform float uSnowStart;
  uniform float uSnowSoftness;
  uniform float uInkStrength;
  uniform float uDistanceFade;

  float terrainBand(float value, float spacing, float width) {
    float coord = value / spacing;
    float edge = abs(fract(coord + 0.5) - 0.5);
    float aa = clamp(fwidth(coord), 0.0001, 0.075);
    return 1.0 - smoothstep(width, width + aa * 1.5, edge);
  }

  float terrainHash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }
`

const fragmentMain = /* glsl */`
  vec3 terrainNormal = normalize(vTerrainWorldNormal);
  float elevation = clamp(
    (vTerrainWorldPosition.y - uElevationMin) / max(uElevationMax - uElevationMin, 0.001),
    0.0,
    1.0
  );
  float upness = clamp(dot(terrainNormal, vec3(0.0, 1.0, 0.0)), 0.0, 1.0);
  float slope = 1.0 - upness;

  // The underlying DEM stays authoritative; its height and normal author the palette.
  vec3 lowColor = mix(uDeepNavy, uNavy, smoothstep(0.0, 0.58, elevation));
  float rockMask = smoothstep(uRustSlopeStart, min(uRustSlopeStart + 0.28, 0.98), slope)
                 * (1.0 - smoothstep(0.82, 1.0, elevation));
  vec3 terrainColor = mix(lowColor, uRust, rockMask * 0.88);

  float snow = smoothstep(uSnowStart, uSnowStart + uSnowSoftness, elevation);
  snow *= smoothstep(0.08, 0.48, upness);
  terrainColor = mix(terrainColor, uIvory, snow * 0.58);

  // True horizontal contour planes intersect the real 3D mesh.
  float contour = terrainBand(vTerrainWorldPosition.y - uElevationMin, uContourSpacing, uContourWidth);
  float majorContour = terrainBand(vTerrainWorldPosition.y - uElevationMin, uContourSpacing * 5.0, uContourWidth * 1.42);
  float contourVisibility = (1.0 - snow * 0.55) * (1.0 - rockMask * 0.2);
  terrainColor = mix(terrainColor, uContourBlue, contour * contourVisibility * 0.22);
  terrainColor = mix(terrainColor, uIvory, majorContour * contourVisibility * 0.06);

  // Sparse slope-aligned ink scoring. Screen-space derivatives keep it stable while moving.
  float hatchCoord = (vTerrainWorldPosition.x * 0.77 + vTerrainWorldPosition.z * 0.39) + vTerrainWorldPosition.y * 0.16;
  float hatch = terrainBand(hatchCoord, 0.72, 0.055);
  float hatchNoise = smoothstep(0.2, 0.82, terrainHash(floor(vTerrainWorldPosition.xz * 0.52)));
  float ink = hatch * hatchNoise * smoothstep(0.24, 0.72, slope) * uInkStrength;
  terrainColor = mix(terrainColor, uDeepNavy, ink);

  // Near contours are crisp; distance resolves into a quiet sculptural silhouette.
  float viewDistance = length(vTerrainWorldPosition - cameraPosition);
  float detailFade = 1.0 - smoothstep(uDistanceFade * 0.55, uDistanceFade, viewDistance);
  terrainColor = mix(lowColor, terrainColor, 0.22 + detailFade * 0.78);
  diffuseColor.rgb = terrainColor;
`

export function createContourTerrainMaterial(options = {}) {
  const material = new THREE.MeshStandardMaterial({
    color: '#071738',
    roughness: 0.94,
    metalness: 0,
    envMapIntensity: 0.2,
  })

  const uniforms = {
    uNavy: { value: new THREE.Color(options.navy ?? '#09245a') },
    uDeepNavy: { value: new THREE.Color(options.deepNavy ?? '#030d25') },
    uContourBlue: { value: new THREE.Color(options.contourBlue ?? '#249cff') },
    uRust: { value: new THREE.Color(options.rust ?? '#c8511b') },
    uIvory: { value: new THREE.Color(options.ivory ?? '#f1ead8') },
    uElevationMin: { value: options.elevationMin ?? -3.8 },
    uElevationMax: { value: options.elevationMax ?? 20.0 },
    uContourSpacing: { value: options.contourSpacing ?? 0.58 },
    uContourWidth: { value: options.contourWidth ?? 0.055 },
    uRustSlopeStart: { value: options.rustSlopeStart ?? 0.44 },
    uSnowStart: { value: options.snowStart ?? 0.78 },
    uSnowSoftness: { value: options.snowSoftness ?? 0.13 },
    uInkStrength: { value: options.inkStrength ?? 0.38 },
    uDistanceFade: { value: options.distanceFade ?? 94 },
  }

  material.userData.terrainUniforms = uniforms
  material.onBeforeCompile = shader => {
    Object.assign(shader.uniforms, uniforms)
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${vertexPars}`)
      .replace('#include <beginnormal_vertex>', `#include <beginnormal_vertex>\n${vertexNormal}`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>\n${vertexPosition}`)
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${fragmentPars}`)
      .replace('#include <color_fragment>', `#include <color_fragment>\n${fragmentMain}`)
  }
  material.customProgramCacheKey = () => 'bv-contour-terrain-v5-2'
  return material
}

export function setTerrainSeason(material, { snowStart, rustSlopeStart, contourBlue } = {}) {
  const uniforms = material?.userData?.terrainUniforms
  if (!uniforms) return
  if (snowStart != null) uniforms.uSnowStart.value = snowStart
  if (rustSlopeStart != null) uniforms.uRustSlopeStart.value = rustSlopeStart
  if (contourBlue) uniforms.uContourBlue.value.set(contourBlue)
}
